import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Plus, Edit, Trash2, Send, Globe, EyeOff, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talentPosts, statuses } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { Loader } from '../UI/LoadingSpinner';
import StatusSelect from '../UI/StatusSelect';
import TalentPostModal from '../TalentPost/TalentPostModal';
import PublishModal from '../TalentPost/PublishModal';
import ConfirmDialog from '../UI/ConfirmDialog';

const TalentPostsTab = forwardRef(({ talentId, showAddButton = true }, ref) => {
  const { hasPermission } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loadingPostId, setLoadingPostId] = useState(null);

  useImperativeHandle(ref, () => ({
    openForm: () => { setEditingPost(null); setModalOpen(true); }
  }));
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishingPost, setPublishingPost] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // post to delete

  const [statusRaw, setStatusRaw] = useState([]);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);

  const statusesLoadedRef = useRef(false);

  const canCreate = hasPermission('talent-service', 'talent_post', 'create');
  const canUpdate = hasPermission('talent-service', 'talent_post', 'update');
  const canDelete = hasPermission('talent-service', 'talent_post', 'delete');

  useEffect(() => {
    if (talentId) fetchPosts();
  }, [talentId]);

  const loadStatuses = async () => {
    if (statusesLoadedRef.current) return;
    statusesLoadedRef.current = true;
    try {
      const res = await statuses.getStatusesByType('talent_post', { isCommon: 1 });
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setStatusRaw(list);
    } catch (err) {
      console.error('Failed to load statuses:', err);
      statusesLoadedRef.current = false;
    }
  };

  const handleStartStatusEdit = (postId) => {
    loadStatuses();
    setEditingStatusId(postId);
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await talentPosts.getAll({ talent_id: talentId, limit: 50 });
      const data = res?.data || [];
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (postId, statusId) => {
    setStatusChangeLoading(postId);
    try {
      await talentPosts.update(postId, { status_id: statusId || null });
      const newStatus = statusRaw.find(s => String(s.id) === String(statusId)) || null;
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, status_id: statusId || null, status: newStatus } : p
      ));
      setEditingStatusId(null);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  const handleDelete = (post) => {
    setDeleteConfirm(post);
  };

  const confirmDelete = async () => {
    setDeletingId(deleteConfirm.id);
    try {
      await talentPosts.remove(deleteConfirm.id);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-4">
      {showAddButton && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary">
            Posts ({posts.length})
          </h3>
          {canCreate && (
            <button
              onClick={() => { setEditingPost(null); setModalOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            >
              <Plus size={14} />
              Add Post
            </button>
          )}
        </div>
      )}

      {posts.length === 0 ? (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-4">No posts yet.</p>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div
              key={post.id}
              className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Link
                      to={`/talent-posts/${post.id}`}
                      className="font-medium text-light-text-primary dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-primary-400 truncate"
                    >
                      {post.name}
                    </Link>
                    {post.publish_status === 'published' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        <Globe size={10} /> Published
                      </span>
                    )}
                    {post.publish_status === 'unpublished' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                        <EyeOff size={10} /> Unpublished
                      </span>
                    )}
                    {post.full_post && (
                      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" title="Has content" />
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">
                    {post.template?.name && (
                      <span>
                        Template:{' '}
                        <Link to={`/talent-templates/${post.template.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                          {post.template.name}
                        </Link>
                      </span>
                    )}
                    {(post.translation?.name || post.translation?.title) && (
                      <span>{post.translation.name || post.translation.title}</span>
                    )}
                    {post.planned_date && <span>Planned: {new Date(post.planned_date).toLocaleDateString()}</span>}
                    {post.publish_date && <span>Published: {new Date(post.publish_date).toLocaleDateString()}</span>}
                    {post.created_at && <span>{new Date(post.created_at).toLocaleDateString()}</span>}
                  </div>

                  <StatusSelect
                    currentStatus={post.status}
                    statusOptions={statusRaw}
                    isEditing={editingStatusId === post.id}
                    isLoading={statusChangeLoading === post.id}
                    onEdit={canUpdate ? () => handleStartStatusEdit(post.id) : undefined}
                    onCancel={() => setEditingStatusId(null)}
                    onChange={(statusId) => handleStatusChange(post.id, statusId)}
                  />

                  {post.link && (
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline mt-1.5"
                    >
                      <LinkIcon size={11} /> {post.link}
                    </a>
                  )}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => { setPublishingPost(post); setPublishModalOpen(true); }}
                    disabled={!post.full_post}
                    className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-green-600 dark:hover:text-green-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={post.full_post ? 'Publish' : 'No content yet'}
                  >
                    <Send size={15} />
                  </button>
                  {canUpdate && (
                    <button
                      onClick={async () => {
                        setLoadingPostId(post.id);
                        try {
                          const res = await talentPosts.getById(post.id);
                          setEditingPost(res?.data || res);
                        } catch { setEditingPost(post); }
                        finally { setLoadingPostId(null); }
                        setModalOpen(true);
                      }}
                      disabled={!!loadingPostId}
                      className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-40 transition-colors"
                      title="Edit"
                    >
                      {loadingPostId === post.id
                        ? <Loader2 size={15} className="animate-spin" />
                        : <Edit size={15} />}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(post)}
                      disabled={deletingId === post.id}
                      className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TalentPostModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingPost(null); }}
        post={editingPost}
        talentId={talentId}
        onSaved={fetchPosts}
      />
      <PublishModal
        isOpen={publishModalOpen}
        onClose={() => { setPublishModalOpen(false); setPublishingPost(null); }}
        post={publishingPost}
        onPublished={fetchPosts}
      />
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        confirmStyle="danger"
        title="Delete Post"
        message={deleteConfirm ? `Are you sure you want to delete this post? This action cannot be undone.` : ''}
        confirmText="Delete"
        isLoading={!!deletingId}
      />
    </div>
  );
});

export default TalentPostsTab;
